// ---------- Component for the google trends panel of the application --------------
// ----------------------------------------------------------------------------------

// import all necessary libraries and components
import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'; // import recharts components
import { useGoogleTrends } from '../hooks/useGoogleTrends'; // import the custom hook which runs the api request
import './GoogleTrendsPanel.css'; // styling import

const COLORS = [ // set the colours for the different keywords
  "#06B6D4", "#22C55E", "#EF4444", "#F59E42", "#A78BFA", "#F472B6", "#FBBF24", "#60A5FA"
];

// component to display google trends data in a line chart format, with options to normalise and select keywords
const GoogleTrendsPanel = ({ timeframe = '3M' }) => {
  const { data: trends, rollingKeywords, loading, error } = useGoogleTrends(timeframe); // call the hook to collect google trends data 
  const [selectedKeywords, setSelectedKeywords] = useState(null); 
  const [normalised, setNormalised] = useState(false);
  const [showKeywordPicker, setShowKeywordPicker] = useState(false);

  // if no keywords selected, show all. Otherwise show selected only
  const activeKeywords = selectedKeywords ?? rollingKeywords;

  // function to toggle keywords in the picker, ensuring at least one is always selected
  const toggleKeyword = (kw) => {
    const current = selectedKeywords ?? rollingKeywords; // treat null as all selected

    if (current.includes(kw)) { // if already selected, remove it 
      if (current.length === 1) return;  // prevent deselecting the last keyword
      setSelectedKeywords(current.filter(k => k !== kw));

    } else {
      setSelectedKeywords([...current, kw]);  // if not selected, add it
    }
  };

  // normalise the data to 0-100 scale
  const chartData = useMemo(() => {
    if (!trends.length || !normalised) return trends; // if not normalising or no data, return raw trends

    // calculate min and max for each keyword to normalise to 0-100 scale
    const ranges = {};

    activeKeywords.forEach(kw => { // loop through the list of keywords to find the min and max for each keyword

      const vals = trends.map(d => d[kw] ?? 0); // loop through the trends data and get the values for the keyword, if no value set to 0
      ranges[kw] = { min: Math.min(...vals), max: Math.max(...vals) }; // store the min and max for the keyword in a dictionary

    });

    return trends.map(row => { // create a dictionary for each row with the date and normalised values for each keyword

      const entry = { date: row.date }; // keep the date as is

      activeKeywords.forEach(kw => { // loop through the list of keywords 
        const { min, max } = ranges[kw]; // use the function to get the min and max for the keyword

        // if the min and max are the same, set to 0 to avoid division by zero, otherwise normalise the value to 0-100 scale
        entry[kw] = max === min ? 0 : Math.round(((row[kw] ?? 0) - min) / (max - min) * 100); // normalise the values

      });

      return entry;

    });
  }, [trends, normalised, activeKeywords]);

  if (loading) { // if the data is loading show a loading screen
    return (
      <div className="google-trends-panel">
        <div className="trends-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3>Google Trends ({timeframe})</h3>
            <span className="trends-source">Source: Google Trends</span>
          </div>
        </div>

        <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '40px' }}>
          Loading trends data...
        </div>
      </div>
    );
  }

  if (error) { // if there is an error show the error message and a retry button
  return (
    <div className="google-trends-panel">
      <div className="trends-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3>Google Trends ({timeframe})</h3>
          <span className="trends-source">Source: Google Trends</span>
        </div>
      </div>

      <div style={{ textAlign: 'center', color: '#EF4444', padding: '40px' }}>
        <p>Error loading trends: {error}</p>
        <button onClick={refetch} style={{ marginTop: '10px', padding: '8px 16px', background: '#06B6D4', color: '#0B1220', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    </div>
  );
}

  if (!trends.length) { // only check trends data, not rollingKeywords
    return (
      <div className="google-trends-panel">
        <div className="trends-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3>Google Trends ({timeframe})</h3>
            <span className="trends-source">Source: Google Trends</span>
          </div>
        </div>

        <div style={{ textAlign: 'center', color: '#EF4444', padding: '40px' }}>
          No Google Trends data available for this timeframe
        </div>
      </div>
    );
  }

  return ( // render the chart if success
    <div className="google-trends-panel">
      <div className="trends-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3>Google Trends ({timeframe}){normalised ? ' — Normalised' : ''}</h3>
          <span className="trends-source">Source: Google Trends</span>
        </div>

        {/* Controls for normalisation and keyword selection */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>

          {/* Normalise button */}
          <button
            className={`trends-control-btn ${normalised ? 'active' : ''}`}
            onClick={() => setNormalised(n => !n)}
            title="Normalise all series to 0-100"
          >
            {normalised ? 'Normalised' : 'Raw'}
          </button>

          {/* Keyword picker */}
          <div style={{ position: 'relative' }}>
            <button
              className="trends-control-btn"
              onClick={() => setShowKeywordPicker(v => !v)}
            >
              Keywords ({activeKeywords.length}/{rollingKeywords.length}) ▾
            </button>

            {showKeywordPicker && (
              <div className="trends-keyword-dropdown">
                <div className="trends-keyword-title">Select Keywords</div>
                {rollingKeywords.map((kw, i) => (
                  <label key={kw} className="trends-keyword-item">
                    <input
                      type="checkbox"
                      checked={activeKeywords.includes(kw)}
                      onChange={() => toggleKeyword(kw)}
                    />
                    <span className="trends-keyword-dot" style={{ background: COLORS[i % COLORS.length] }} />
                    {/* clean up the label to remove rolling and underscores, if the label is empty use the original keyword */}
                    {kw.replace(/_rolling|rolling/g, '').replace(/_/g, ' ').trim() || kw} 
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Plot the chart */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%"> {/* make the chart responsive to the container size */}
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2933" /> {/* set grid style */}

            <XAxis 
              dataKey="date" 
              stroke="#9CA3AF" 
              style={{ fontSize: '12px' }}
              tick={{ fontSize: 12 }}
            />

            <YAxis 
              stroke="#9CA3AF" 
              style={{ fontSize: '12px' }}
              tick={{ fontSize: 12 }}
              domain={normalised ? [0, 100] : ['auto', 'auto']} // set the y-axis domain to 0-100 if normalised, otherwise auto
              tickFormatter={normalised ? (v) => `${v}` : undefined}  
            />

            <Tooltip  // sets the style for the hovering over data
              contentStyle={{
                backgroundColor: '#1F2933',
                border: '1px solid #111827',
                borderRadius: '4px',
                color: '#E5E7EB'
              }}
              formatter={(val, name) => [normalised ? `${val}` : val, name]} // format the tooltip to show the value and the keyword name
            />

            <Legend /> {/* add legend to interpret the different lines */}
            {activeKeywords.map((kw, i) => ( // loop through all keywords and display them
              <Line
                key={kw}
                type="monotone"
                dataKey={kw}
                stroke={COLORS[rollingKeywords.indexOf(kw) % COLORS.length]} // set the colour of the line based on the keyword index
                strokeWidth={2}
                dot={false}
                name={kw.replace(/_rolling|rolling/g, '').replace(/_/g, ' ').trim() || kw} // same clearning as before for the label
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GoogleTrendsPanel;


